import React, { useState } from 'react'
import { DialogContent, Dialog as MuiDialog, Box, Button, Typography, IconButton, DialogTitle } from '@mui/material'
import { getDocNameWithActor, useDeleteDocData } from 'hooks/useDocListData'
import { useDocPreview } from 'hooks/useDocPreview'
import UploadPreview from 'components/UploadPreview'
import SidesUpload from 'components/SidesUpload'
import UploadHeader from 'components/UploadHeader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/free-regular-svg-icons'
import { docUploadAnalytics } from 'analytics/docUploadAnalytics'
import closeIcon from 'images/closeIcon.svg'
import AcceptSpinner from './AcceptSpinner'
import { MESSAGES } from '../messages/messages'

const UploadPreviewDialog = () => {
    const {
        isDialogOpen,
        preview,
        backSidePreview,
        closeDialog,
        isSidesPreview,
        loadingProgress,
        isLoading,
        handleDocUpload
    } = useDocPreview()
    const { documentName, documentId, actorRole } = preview
    const isEditMode = !!documentId
    const isUploadEnabled = isSidesPreview ? preview.base64Data && backSidePreview.base64Data : true
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isProcess, setIsProcess] = useState(false)

    // This function is called when the delete button is clicked.
    const handleDeleteClick = () => {
        setIsProcess(true)
        handleDeleteDoc()
    }

    const { mutateAsync: deleteDoc } = useDeleteDocData()

    const showDeleteDialog = () => {
        docUploadAnalytics.track('delete', getDocNameWithActor(documentName, actorRole))
        toggleDeleteDialog()
    }

    const toggleDeleteDialog = () => {
        setIsDeleteDialogOpen((old) => !old)
    }

    const handleDeleteDoc = async () => {
        docUploadAnalytics.track('confirmDelete', getDocNameWithActor(documentName, actorRole))
        await deleteDoc(documentId)
        setIsProcess(false)
        toggleDeleteDialog()
        closeDialog()
    }

    const handleCancel = () => {
        docUploadAnalytics.track('cancel', getDocNameWithActor(documentName, actorRole))
        closeDialog()
    }

    const footerContent = isEditMode ? (
        <>
            <Button data-testid="cancel" variant={'text'} startIcon={''} disableRipple onClick={handleCancel}>
                {MESSAGES.cancel}
            </Button>

            <Button
                data-testid="delete"
                variant="outlined"
                disableRipple
                startIcon={<FontAwesomeIcon icon={faTrashCan} />}
                onClick={showDeleteDialog}
            >
                {MESSAGES.delete}
            </Button>

            <MuiDialog
                open={isDialogOpen && isDeleteDialogOpen}
                disableEscapeKeyDown={true}
                aria-labelledby="delete_document_modal_title"
                aria-describedby="delete_document_modal_content"
                data-testid="delete-document-modal-dialog"
                sx={{
                    color: 'common.fordBlue.main',
                    zIndex: 3000,
                    textAlign: 'left',
                    padding: '0px',
                    '.MuiModal-backdrop': {
                        backgroundColor: 'action.disabledBackground'
                    },
                    '.MuiDialog-paper': {
                        color: 'common.fordBlue.main',
                        backgroundColor: 'background.default',
                        borderRadius: { xs: 0, smd: 4 },
                        maxWidth: { xs: 390, smd: 480 },
                        width: '100%',
                        height: 'auto',
                        margin: 4,
                        maxHeight: { xs: '100%', sm: '98%' }
                    }
                }}
            >
                <DialogTitle
                    id="delete_document_modal_title"
                    data-testid="delete-document-modal-header"
                    sx={{
                        p: 4
                    }}
                >
                    <Typography
                        tabIndex={0}
                        data-testid="delete-document-modal-title"
                        sx={{
                            typography: 'mediumTitle',
                            color: 'common.charcoal.main'
                        }}
                    >
                        {MESSAGES.delete_document_modal_title}
                    </Typography>
                    <IconButton
                        id="close"
                        aria-label="close"
                        onClick={toggleDeleteDialog}
                        sx={{
                            color: 'common.charcoal.main',
                            p: 2,
                            position: 'absolute',
                            right: '5px',
                            top: '5px'
                        }}
                    >
                        <img src={closeIcon} alt={'close modal'} width={24} height={24} />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    tabIndex={0}
                    data-testid="delete-document-modal-content"
                    sx={{
                        px: 4,
                        pb: 0,
                        typography: { xs: 'regularBody2', md: 'regularBody1' },
                        color: 'common.charcoal.main',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 7
                    }}
                >
                    {MESSAGES.delete_document_modal_content}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center',
                            component: 'div',
                            justifyContent: 'flex-end',
                            py: 4
                        }}
                    >
                        <Button variant="outlined" data-testid="cancel" disableRipple onClick={toggleDeleteDialog}>
                            {MESSAGES.cancel}
                        </Button>
                        <Button variant="contained" data-testid="delete" disableRipple onClick={handleDeleteClick}>
                            {MESSAGES.delete}
                        </Button>
                    </Box>
                </DialogContent>
            </MuiDialog>
        </>
    ) : (
        <>
            <Button
                data-testid="cancel"
                variant={'outlined'}
                disableRipple
                onClick={handleCancel}
                sx={{
                    p: '12px 14px',
                    height: '40px',
                    minWidth: { xs: '96px' }
                }}
            >
                {MESSAGES.cancel}
            </Button>

            <Button
                data-testid="upload"
                variant="contained"
                disableRipple
                disabled={!isUploadEnabled}
                onClick={handleDocUpload}
                sx={{
                    p: '12px 14px',
                    height: '40px',
                    minWidth: { xs: '96px' }
                }}
            >
                {MESSAGES.upload}
            </Button>
        </>
    )

    return (
        <>
            {isLoading && <AcceptSpinner />}
            {isProcess && <AcceptSpinner />}
            <MuiDialog
                fullScreen
                open={isDialogOpen}
                onClose={closeDialog}
                aria-modal="true"
                aria-labelledby="upload-preview-dialog"
                aria-describedby="upload-preview-dialog"
                data-testid="upload-preview-dialog"
                sx={{
                    color: 'common.fordBlue.main',
                    textAlign: 'left',
                    zIndex: 3000,
                    '.MuiDialog-paper': {
                        backgroundColor: 'background.default'
                    }
                }}
            >
                <UploadHeader headerName={documentName} clickHandler={closeDialog} />
                <DialogContent
                    data-testid="upload-preview-content"
                    sx={{
                        backgroundColor: 'common.gray.9',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        position: 'relative',
                        p: 0,
                        pt: 0
                    }}
                >
                    {isSidesPreview ? <SidesUpload /> : <UploadPreview />}
                    <Box
                        component="footer"
                        position="sticky"
                        data-testid="upload-preview-footer"
                        sx={{
                            backgroundColor: 'background.default',
                            borderTop: (theme) => `1px solid ${theme.palette.common.gray.dark}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 4,
                            bottom: 0,
                            py: 4,
                            px: 6,
                            mt: 'auto',
                            zIndex: 3000
                        }}
                    >
                        {footerContent}
                    </Box>
                </DialogContent>
            </MuiDialog>
        </>
    )
}

export default UploadPreviewDialog
